import { relations } from 'drizzle-orm';
import { boolean, index, integer, pgEnum, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import { Status } from '../../util/schemas.js';
import { addresses } from './addresses.js';
import { locations } from './locations.js';
import { userAccessLocation } from './user-access-location.js';
import { uuidv7 } from './utils/uuidv7.js';
export const userStatusEnum = pgEnum('user_status', [Status.enum.active, Status.enum.inactive]);
export const user = pgTable('user', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    name: text('name').notNull(),
    email: text('email').notNull().unique(),
    emailVerified: boolean('email_verified').default(false).notNull(),
    image: text('image'),
    username: text('username').unique(),
    displayUsername: text('display_username'),
    firstName: text('first_name'),
    lastName: text('last_name'),
    role: text('role'),
    title: text('title'),
    hospitalSystemId: text('hospital_system_id'),
    nationalPhysicianNumber: text('national_physician_number'),
    hospitalPhysicianNumber: text('hospital_physician_number'),
    /**
     * An "Active user on Cypro" flag replacing old Cypro's "prescribing physician" flag.
     *
     * When`false`, the user still exists (e.g. external doctors can be assigned to therapies) but cannot log in and is hidden from user lists.
     */
    isActiveOnCypro: boolean('is_active_on_cypro').notNull().default(true),
    addressId: uuid('address_id').references(() => addresses.id, { onDelete: 'set null' }),
    banned: boolean('banned').default(false),
    banReason: text('ban_reason'),
    banExpires: timestamp('ban_expires'),
    status: userStatusEnum('status').default(Status.enum.active),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
        .defaultNow()
        .$onUpdate(() => /* @__PURE__ */ new Date())
        .notNull(),
    deletedAt: timestamp('deleted_at'),
    // createdBy can be null on the `user` table because the first user won't be created by someone else
    createdBy: uuid('created_by').references(() => user.id),
    updatedBy: uuid('updated_by').references(() => user.id),
    deletedBy: uuid('deleted_by').references(() => user.id),
}, t => [index().on(t.email)]);
export const session = pgTable('session', {
    id: uuidv7('id').primaryKey(),
    token: text('token').notNull().unique(),
    expiresAt: timestamp('expires_at').notNull(),
    ipAddress: text('ip_address'),
    userAgent: text('user_agent'),
    userId: uuid('user_id')
        .notNull()
        .references(() => user.id, { onDelete: 'cascade' }),
    impersonatedBy: text('impersonated_by'),
    locationId: uuid('active_location_id').references(() => locations.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
        .$onUpdate(() => /* @__PURE__ */ new Date())
        .notNull(),
}, t => [index().on(t.userId, t.token)]);
export const sessionRelations = relations(session, ({ one }) => ({
    user: one(user, {
        fields: [session.userId],
        references: [user.id],
    }),
    location: one(locations, {
        fields: [session.locationId],
        references: [locations.id],
    }),
}));
export const account = pgTable('account', {
    id: uuidv7('id').primaryKey(),
    accountId: text('account_id').notNull(),
    providerId: text('provider_id').notNull(),
    userId: uuid('user_id')
        .notNull()
        .references(() => user.id, { onDelete: 'cascade' }),
    accessToken: text('access_token'),
    refreshToken: text('refresh_token'),
    idToken: text('id_token'),
    accessTokenExpiresAt: timestamp('access_token_expires_at'),
    refreshTokenExpiresAt: timestamp('refresh_token_expires_at'),
    scope: text('scope'),
    password: text('password'),
    /**
     * Timestamp meaning when the user set their password after being asked to do so by the system.
     */
    passwordSetupCompletedAt: timestamp('password_setup_completed_at'),
    /**
     * In comparison with `passwordSetupCompletedAt`, this field holds the timestamp when the user set their password, regardless of being asked to do so or not.
     */
    passwordSetAt: timestamp('password_set_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
        .$onUpdate(() => /* @__PURE__ */ new Date())
        .notNull(),
}, t => [index().on(t.userId)]);
export const verification = pgTable('verification', {
    id: uuidv7('id').primaryKey(),
    identifier: text('identifier').notNull(),
    value: text('value').notNull(),
    expiresAt: timestamp('expires_at').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at')
        .defaultNow()
        .$onUpdate(() => /* @__PURE__ */ new Date())
        .notNull(),
}, t => [index().on(t.identifier)]);
export const jwks = pgTable('jwks', {
    id: uuidv7('id').primaryKey(),
    publicKey: text('public_key').notNull(),
    privateKey: text('private_key').notNull(),
    createdAt: timestamp('created_at').notNull(),
    // added by better-auth 1.7's JWT plugin
    expiresAt: timestamp('expires_at'),
    alg: text('alg'),
    crv: text('crv'),
});
// Better Auth 1.7's organization plugin requires the invitation table even when invitations are not used.
export const invitation = pgTable('invitation', {
    id: uuidv7('id').primaryKey(),
    organizationId: uuid('organization_id').notNull(),
    email: text('email').notNull(),
    role: text('role').default('member'),
    status: text('status').notNull().default('pending'),
    expiresAt: timestamp('expires_at').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    inviterId: uuid('inviter_id')
        .notNull()
        .references(() => user.id, { onDelete: 'cascade' }),
}, t => [index('idx_invitation_organization').on(t.organizationId), index('idx_invitation_email').on(t.email)]);
export const apikey = pgTable('apikey', {
    id: uuidv7('id').primaryKey(),
    name: text('name'),
    start: text('start'),
    prefix: text('prefix'),
    key: text('key').notNull(),
    userId: uuid('user_id')
        .notNull()
        .references(() => user.id, { onDelete: 'cascade' }),
    refillInterval: integer('refill_interval'),
    refillAmount: integer('refill_amount'),
    lastRefillAt: timestamp('last_refill_at'),
    enabled: boolean('enabled').default(true),
    rateLimitEnabled: boolean('rate_limit_enabled').default(true),
    rateLimitTimeWindow: integer('rate_limit_time_window').default(86400000),
    rateLimitMax: integer('rate_limit_max').default(10),
    requestCount: integer('request_count').default(0),
    remaining: integer('remaining'),
    lastRequest: timestamp('last_request'),
    permissions: text('permissions'),
    metadata: text('metadata'),
    expiresAt: timestamp('expires_at'),
    createdAt: timestamp('created_at').notNull(),
    updatedAt: timestamp('updated_at').notNull(),
});
export const userRelations = relations(user, ({ one, many }) => ({
    created: one(user, {
        fields: [user.createdBy],
        references: [user.id],
    }),
    updated: one(user, {
        fields: [user.updatedBy],
        references: [user.id],
    }),
    address: one(addresses, {
        fields: [user.addressId],
        references: [addresses.id],
    }),
    userAccessLocations: many(userAccessLocation),
    sessions: many(session),
    accounts: many(account),
}));
export const accountRelations = relations(account, ({ one }) => ({
    user: one(user, {
        fields: [account.userId],
        references: [user.id],
    }),
}));
//# sourceMappingURL=auth.js.map